// Disabling import sort because it breaks the app
// eslint-disable-next-line simple-import-sort/imports
import { createStore } from 'vuex';
import md5 from 'md5';
import LogRocket from 'logrocket';
import createPlugin from 'logrocket-vuex';

import LocalCache from '@/utils/LocalCache';
import Environment from '@/common/enums/environment.enum';
import UserRoles from '@/common/enums/user-roles.enum';
import orgAppStore from '@/org-app/store';
import exchAppStore from '@/exch-app/store';
import i18n from '@/vueI18n';
import { environment, titlecase, uppercase } from '@/globals';
import modelsStore from './modules/models';
import orgExchDispatchStore from './modules/orgexchdispatcher';
import mutations from './mutations';
import actions from './actions';
import initialState from './state';
import { StoreInternals } from './store-types';

const getters = {
  authenticated: (state) => state.authenticated,
  user: (state) => state.user,
  userRole: (state) => state.userRole,
  isGlobalAdmin: (state) => state.user && state.user.isGlobalAdmin,
  isSalesPlanner: (state) => state.userRole && state.userRole.code === UserRoles.SALES_PLANNER,
  isPlannerReserveCampaignIDRole: (state) =>
    state.userRole && state.userRole.code === UserRoles.PLANNER_RESERVE_CAMPAIGN_ID,
  gravatar: (state) => md5(state.user === null || state.user.email === undefined ? '' : state.user.email),
  isPublicRoute: (state) => state.isPublicRoute,
  shouldBlurMoneyAmounts: () =>
    LocalCache.get('shouldBlurMoneyAmounts') != null &&
    LocalCache.get('shouldBlurMoneyAmounts') !== 'false' &&
    LocalCache.get('shouldBlurMoneyAmounts') !== false,
  headerContent: (state, getters) => {
    if (getters.hasChosenExchange) {
      return {
        name:
          state.exchApp.exchangeSettings != null && state.exchApp.exchangeSettings.exchangeBrandingName != null
            ? state.exchApp.exchangeSettings.exchangeBrandingName
            : titlecase(i18n.global.t('names.exchange')),
      };
    }
    const isDemandOrganization = getters['orgApp/isDemandOrganization'];
    const isSupplyOrganization = getters['orgApp/isSupplyOrganization'];
    const isCuratorOrganization = getters['orgApp/isCuratorOrganization'];
    const isSidekickOrganization = getters['orgApp/isSidekickOrganization'];
    if (isDemandOrganization) {
      // If organizationSettings is null then return null
      if (state.orgApp.organizationSettings == null) {
        return null;
      }
      if (isSidekickOrganization) {
        return {
          name: titlecase(i18n.global.t('names.sidekick')),
        };
      }
      return {
        // Return first variable for name and image, if it's null return second variable after ||
        name: state.orgApp.organizationSettings.dspBrandingName || uppercase(i18n.global.t('names.dsp')),
        image: state.orgApp.organizationSettings.dspBrandingImage || undefined,
      };
    }
    if (isSupplyOrganization) {
      return {
        name:
          state.orgApp.organizationSettings != null && state.orgApp.organizationSettings.sspBrandingName != null
            ? state.orgApp.organizationSettings.sspBrandingName
            : titlecase(i18n.global.t('names.mediaOwner')),
      };
    }
    if (isCuratorOrganization) {
      return {
        name: titlecase(i18n.global.t('names.curate')),
      };
    }
    return null;
  },
  membershipName: (state, getters) => {
    if (!getters.hasChosenMembership) {
      return '';
    }
    return getters.hasChosenOrganization
      ? state.orgApp.organization && state.orgApp.organization.name
      : state.exchApp.exchange && state.exchApp.exchange.name;
  },
  hasChosenOrganization: (state) => state.chosenMembershipType === 'org',
  hasChosenExchange: (state) => state.chosenMembershipType === 'exch',
  hasChosenMembership: (state) => state.chosenMembershipType != null,
  permissions: (state, getters) => {
    if (!getters.hasChosenMembership) {
      return null;
    }

    return getters.hasChosenOrganization ? state.orgApp.permissions : state.exchApp.permissions;
  },
  membershipSettings(state, getters) {
    if (getters.hasChosenExchange) {
      return state.exchApp.exchangeSettings;
    }
    if (getters.hasChosenOrganization) {
      return state.orgApp.organizationSettings;
    }
    return null;
  },
  isHivestackUser: (state) => state.user?.email?.endsWith('@hivestack.com'),
  targetedUnitsMax: (state, getters) => getters.membershipSettings?.implicitTargetedUnitsMax,
  importedUnitsMax: (state, getters) => getters.membershipSettings?.explicitTargetedUnitsMax,
};

export type RootGetters = {
  [K in keyof typeof getters]: (state: typeof initialState) => ReturnType<(typeof getters)[K]>;
};

const store: StoreInternals = createStore({
  strict: false,
  state: initialState,
  mutations,
  actions,
  modules: {
    orgApp: orgAppStore,
    exchApp: exchAppStore,
    models: modelsStore,
    orgexchdispatcher: orgExchDispatchStore,
  },
  getters,
  plugins: environment === Environment.STAGING || environment === Environment.UAT ? [createPlugin(LogRocket)] : [],
}) as StoreInternals;

export default store;
